import React, {
  memo,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  RefObject,
  createRef,
  ComponentRef,
} from 'react';
import {
  Box,
  Breakpoint,
  Button,
  ScrollView,
  Text,
  useBreakpoint,
} from '@betty/moonlight';
import { Trans } from '@betty/i18n';
import RegistrationField from '../../RegistrationField/RegistrationField';
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  Easing,
} from 'react-native-reanimated';

import { RegistrationStepProps } from '../RegistrationStep.types';
import { useNavigation } from '@betty/navigation';
import {
  selectFormErrorsByFields,
  selectRegistrationConfig,
  selectRegistrationFormData,
  selectRegistrationStepIndex,
  setStepIndex,
  updateRegistrationData,
} from '../../../state';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import { Dimensions, TextInput, LayoutChangeEvent, View } from 'react-native';
import AdditionalRegisterInfo from '../../AdditionalRegisterInfo/AdditionalRegisterInfo';

const { height: screenHeight } = Dimensions.get('window');

function DefaultRegistrationStep({
  step,
  onSubmit: onSubmitProp,
  submitDataState,
}: RegistrationStepProps) {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();
  const [showErrors, setShowErrors] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const scrollViewRef = useRef<ComponentRef<typeof ScrollView>>(null);
  const config = useSelector(selectRegistrationConfig);
  const data = useSelector(selectRegistrationFormData);
  const errors = useSelector(selectFormErrorsByFields(step.fields || []));
  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
  const [hasScrolledForErrors, setHasScrolledForErrors] = useState(false);
  const stepIndex = useSelector(selectRegistrationStepIndex);
  const [buttonContainerHeight, setButtonContainerHeight] = useState(0);

  const isMobile = breakpoint === Breakpoint.MOBILE;

  const opacity = useSharedValue(0);
  const translateY = useSharedValue(50);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    };
  });

  const buttonTopPosition = useMemo(() => {
    return contentHeight - 10;
  }, [contentHeight]);

  const handleButtonContainerLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout;
      setButtonContainerHeight(height);
    },
    [],
  );

  const shouldUseAbsolutePositioning = useMemo(() => {
    return (
      contentHeight + buttonContainerHeight + 120 < screenHeight && !isMobile
    );
  }, [contentHeight, buttonContainerHeight, isMobile]);

  const buttonContainerStyle = useMemo(
    () => ({
      ...(shouldUseAbsolutePositioning
        ? {
            position: 'absolute' as const,
            top: buttonTopPosition,
          }
        : {}),
      left: 0,
      right: 0,
      alignItems: 'center' as const,
      backgroundColor: 'transparent',
    }),
    [shouldUseAbsolutePositioning, buttonTopPosition],
  );

  const descriptionComponents = useMemo(
    () => ({
      textLink: (
        <Text
          variant="body-m"
          color="level_2"
          onPress={() =>
            navigation.navigate('MiscScreen', {
              path: '/responsible-gaming',
              openInModal: true,
            })
          }
        />
      ),
    }),
    [],
  );

  const onSubmit = useCallback(() => {
    if (hasErrors) {
      setShowErrors(true);
      setHasScrolledForErrors(false);
      return;
    }

    onSubmitProp();
  }, [hasErrors, onSubmitProp]);

  const handleContentLayoutCustom = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setContentHeight(height);
  }, []);

  useEffect(() => {
    if (showErrors && hasErrors && contentHeight > 0 && !hasScrolledForErrors) {
      setTimeout(() => {
        const extraOffset = contentHeight + 120 > screenHeight ? 200 : 0;
        const scrollToY = Math.max(
          0,
          contentHeight + 120 - screenHeight + extraOffset,
        );
        scrollViewRef.current?.scrollTo({
          y: scrollToY,
          animated: true,
        });
        setHasScrolledForErrors(true);
      }, 100);
    }
  }, [showErrors, hasErrors, contentHeight, hasScrolledForErrors]);

  useEffect(() => {
    opacity.value = 0;
    translateY.value = 50;

    opacity.value = withTiming(1, {
      duration: 200,
      easing: Easing.out(Easing.ease),
    });
    translateY.value = withTiming(0, {
      duration: 200,
      easing: Easing.out(Easing.ease),
    });
  }, [step.id, opacity, translateY]);

  const fieldRefs = useRef<{ [key: number]: RefObject<TextInput> }>(
    step?.fields?.reduce(
      (acc, _, idx) => ({
        ...acc,
        [idx]: createRef<TextInput>(),
      }),
      {},
    ) || {},
  );

  return (
    <Box flex={1} width="100%">
      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        animated={true}
        style={{ flex: 1 }}
      >
        <Box alignItems="center" width="100%">
          <Box
            width={{
              mobile: '100%',
              tablet: '100%',
              desktop: 760,
            }}
          >
            <Box
              padding="space-400"
              animatedStyle={animatedStyles}
              onLayout={handleContentLayoutCustom}
            >
              <Box marginBottom="space-100" marginTop="space-300">
                {step.stepTitleNS && step.stepTitleKey ? (
                  <Text
                    variant="h5"
                    color="darkSurfacePrimary"
                    locNS={step.stepTitleNS}
                    locKey={step.stepTitleKey}
                    marginBottom="space-300"
                  />
                ) : null}
                {step.stepDescriptionNS && step.stepDescriptionKey ? (
                  <Text
                    variant="body-m"
                    color="darkSurfacePrimary"
                    marginBottom="space-300"
                  >
                    <Trans
                      i18nKey={step.stepDescriptionKey}
                      ns={step.stepDescriptionNS}
                      components={descriptionComponents}
                    />
                  </Text>
                ) : null}
              </Box>
              <Box width="100%">
                {step.fields?.map((field, index) => (
                  <Box key={field} marginBottom="space-400">
                    <RegistrationField
                      name={field}
                      autoFocus={index === 0}
                      ref={fieldRefs.current[index]}
                      enterKeyHint={
                        index < (step.fields?.length ?? 1) - 1 ? 'next' : 'go'
                      }
                      onSubmitEditing={() => {
                        if (step.fields && index < step.fields.length - 1) {
                          fieldRefs.current[index + 1]?.current?.focus();
                        } else {
                          if (hasErrors) {
                            setShowErrors(true);
                            setHasScrolledForErrors(false);
                            return;
                          }
                          dispatch(setStepIndex(stepIndex + 1));
                        }
                      }}
                      {...config.fields[field]}
                      value={data[field as keyof typeof data]}
                      error={
                        showErrors
                          ? errors[field as keyof typeof errors]
                          : undefined
                      }
                      onValue={(value) => {
                        dispatch(updateRegistrationData({ [field]: value }));
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </ScrollView>
      <View style={buttonContainerStyle} onLayout={handleButtonContainerLayout}>
        <Box
          width={{
            mobile: '100%',
            tablet: '100%',
            desktop: 760,
          }}
          marginTop={{
            mobile: 'space-0',
            tablet: 'space-200',
            desktop: 'space-400',
          }}
          paddingHorizontal="space-400"
        >
          <Button
            onPress={onSubmit}
            locNS="registration"
            locKey={step.cta || 'nextButton'}
            loading={submitDataState === 'pending'}
            disabled={showErrors && hasErrors}
            variant="primary"
            size="large"
          />
        </Box>
        <AdditionalRegisterInfo />
      </View>
    </Box>
  );
}

export default memo(DefaultRegistrationStep);
